import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCustomerOrder } from '../../lib/customerApi';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Package } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

const statusConfig = {
  pending: { label: '受付中', className: 'bg-amber-100 text-amber-800' },
  confirmed: { label: '確定', className: 'bg-blue-100 text-blue-800' },
  shipped: { label: '発送済', className: 'bg-purple-100 text-purple-800' },
  completed: { label: '完了', className: 'bg-emerald-100 text-emerald-800' },
  cancelled: { label: 'キャンセル', className: 'bg-red-100 text-red-800' },
};

export const CustomerOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const res = await getCustomerOrder(id);
      setOrder(res.data);
    } catch (error) {
      navigate('/customer/orders');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!order) return null;

  const status = statusConfig[order.status] || statusConfig.pending;

  return (
    <div className="space-y-6" data-testid="customer-order-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/customer/orders')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{order.order_number}</h1>
          <p className="text-slate-500">
            {format(new Date(order.created_at), 'yyyy年MM月dd日 HH:mm', { locale: ja })}
          </p>
        </div>
        <Badge className={`${status.className} ml-auto`}>{status.label}</Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Order Items */}
        <div className="lg:col-span-2">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">注文明細</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {order.items?.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 p-4">
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Package className="h-6 w-6 text-slate-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900">{item.item_name}</p>
                      <p className="text-xs text-slate-500">
                        {item.origin && <span>{item.origin}</span>}
                        {item.origin && item.spec && <span> / </span>}
                        {item.spec && <span>{item.spec}</span>}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">{item.quantity}{item.unit} × ¥{item.unit_price?.toLocaleString()}</p>
                      <p className="font-mono font-medium text-slate-900">¥{item.subtotal?.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-slate-50 p-4 flex justify-between items-center">
                <span className="font-semibold">合計金額</span>
                <span className="font-mono font-bold text-xl text-blue-600">
                  ¥{order.total_amount.toLocaleString()}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Order Info */}
        <div>
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">注文情報</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-slate-500">注文番号</p>
                <p className="font-mono font-medium">{order.order_number}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">ステータス</p>
                <Badge className={status.className}>{status.label}</Badge>
              </div>
              {order.delivery_date && (
                <div>
                  <p className="text-sm text-slate-500">配送希望日</p>
                  <p className="font-medium">{order.delivery_date}</p>
                </div>
              )}
              {order.notes && (
                <div>
                  <p className="text-sm text-slate-500">申し送り事項</p>
                  <p className="text-slate-700 bg-slate-50 p-3 rounded-lg mt-1">{order.notes}</p>
                </div>
              )}
              <div className="border-t border-slate-200 pt-4">
                <p className="text-sm text-slate-500">注文日時</p>
                <p>{format(new Date(order.created_at), 'yyyy年MM月dd日 HH:mm', { locale: ja })}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
